/* eslint-disable no-debugger */
import React, { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { merge, get, isEqual, cloneDeep, omit } from 'lodash';
import { decodeJwt } from 'jose';

import { setTimumApiHost } from '@timum/timum_pdk/src';

import {
  selectPermissions,
  setAppConfig,
  setFcConfig,
  setMuiTheme,
  selectProduct,
} from '../../slices/appState';
import { isSlicePermitted } from '../../utils/permissions';
import { primary, STANDARD_FIELD_NAMES } from '../../defaults';
import {
  ensureArray,
  transformRefArray,
  normalizeHeight,
  getCookie,
  getVisibleTld,
  isCountryCodeTLD,
  sortFieldsByIndex,
  removeNullFields,
  pickDataFetchDeps,
} from '../../utils/configUtils';
import { isPresetThemeSync } from '../../themePresetHashes';

const timumDomains =
  'timum\\.de|timum\\.at|timum\\.com|timum\\.fr|timum\\.io|timum\\.es|timum\\.it|timum\\.co.uktimum\\.nl|timum\\.eu|timum\\.info|timum\\.org|timum\\.me';
const allowedOrigin = `(${timumDomains}|localhost(:[0-9]+)?|127\\.0\\.0\\.1(:[0-9]+)?)`;
const allowedOriginRegex = new RegExp(
  `^https?://([a-zA-Z0-9-]+\\.)*${allowedOrigin}$`,
);

function removeUndefinedFields(fields, mergedFields) {
  if (!fields) return;
  const fieldNames = STANDARD_FIELD_NAMES;
  for (const fieldName of fieldNames) {
    if (Object.hasOwn(fields, fieldName) && fields[fieldName] === undefined) {
      delete mergedFields[fieldName];
    }
  }
}

function applyMobileDefaults(tld, mergedAppConfig, givenAppConfig) {
  // omit if field was turned off deliberatly
  const mobileValue = get(givenAppConfig, 'fields.mobile', undefined);
  if (mobileValue === undefined) {
    return;
  }
  if (typeof tld !== 'string') return;
  if (tld === 'localhost') return;

  // enhance preferredCountries if it is unchanged from the default...
  if (!get(givenAppConfig, 'fields.mobile.preferredCountries', undefined)) {
    // ... and the tld is parseable
    if (isCountryCodeTLD(tld.toUpperCase())) {
      mergedAppConfig.fields.mobile.preferredCountries = [
        tld.toUpperCase(),
        ...primary.appConfig.fields.mobile.preferredCountries,
      ];
    }
  }
  // Set defaultCountry if it doesn't exist (is null/undefined/falsy)...
  if (!get(givenAppConfig, 'fields.mobile.defaultCountry', undefined)) {
    // ... and the tld is parseable
    if (isCountryCodeTLD(tld.toUpperCase())) {
      mergedAppConfig.fields.mobile.defaultCountry = tld.toUpperCase();
    }
  }
}

function transformRef(ref, platform, prvUuid) {
  if (
    !ref.includes('@') &&
    !ref.match(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    )
  ) {
    if (!platform) {
      console.warn(
        "One or more ref parameters aren't fully qualified AND you have not provided a `platform` prop which allows timum to do it for you. " +
          "Most likely timum won't find anything. " +
          'Please either add a platform prop to your configuration or use a full reference in either the ref prop or a ref url parameter.',
      );
    } else {
      return `${ref}${prvUuid ? '@' + prvUuid : ''}@${platform}`;
    }
  }

  return ref;
}

function addPostMessageCallbacks(appConfig) {
  const postMessage = (type, payload = {}) => {
    window?.parent?.postMessage(
      { origin: 'bookingjs', type, ...payload },
      appConfig.postMessageTarget,
    );
  };

  // Factory for callbacks that spread their payload directly
  const createSpreadCallback = (type) => (payload) =>
    postMessage(type, payload);

  // Factory for callbacks that wrap arg as 'data'
  const createDataCallback = (type) => (data) => postMessage(type, { data });

  // Factory for callbacks that wrap arg as 'error'
  const createErrorCallback = (type) => (error) => postMessage(type, { error });

  // Factory for callbacks that wrap arg as 'timeslot'
  const createTimeslotCallback = (type) => (tsl) =>
    postMessage(type, { timeslot: tsl });

  // Factory for callbacks with no args
  const createSimpleCallback = (type) => () => postMessage(type);

  appConfig.callbacks = {
    // Booking lifecycle callbacks (spread payload)
    createBookingStarted: createSpreadCallback('createBookingStarted'),
    createBookingSuccessful: createSpreadCallback('createBookingSuccessful'),
    createBookingFailed: createSpreadCallback('createBookingFailed'),

    // Cancelation lifecycle callbacks (spread payload)
    cancelationStarted: createSpreadCallback('cancelationStarted'),
    cancelationSuccessful: createSpreadCallback('cancelationSuccessful'),
    cancelationFailed: createSpreadCallback('cancelationFailed'),

    // Fetch success callbacks (wrap as data)
    fetchingPublicDataSucceeded: createDataCallback(
      'fetchingPublicDataSucceeded',
    ),
    fetchingProductsSucceeded: createDataCallback('fetchingProductsSucceeded'),
    fetchingBookablesSucceeded: createDataCallback(
      'fetchingBookablesSucceeded',
    ),

    // Fetch failure callbacks (wrap as error)
    fetchingPublicDataFailed: createErrorCallback('fetchingPublicDataFailed'),
    fetchingProductsFailed: createErrorCallback('fetchingProductsFailed'),
    fetchingBookablesFailed: createErrorCallback('fetchingBookablesFailed'),

    // Dialog open/close with timeslot
    openedBookingPage: createTimeslotCallback('openedBookingPage'),
    openedCancelPage: createTimeslotCallback('openedCancelPage'),
    closedBookingPage: createTimeslotCallback('closedBookingPage'),
    closedCancelPage: createTimeslotCallback('closedCancelPage'),

    // Simple dialog open/close (no args)
    openedProductSelection: createSimpleCallback('openedProductSelection'),
    openedResourceSelection: createSimpleCallback('openedResourceSelection'),
    openedConfirmationPage: createSimpleCallback('openedConfirmationPage'),
    closedProductSelection: createSimpleCallback('closedProductSelection'),
    closedResourceSelection: createSimpleCallback('closedResourceSelection'),
    closedConfirmationPage: createSimpleCallback('closedConfirmationPage'),
  };
}

function getJwtConfig(jwt) {
  try {
    return decodeJwt(jwt);
  } catch (error) {
    console.error('Error decrypting JWT:', error);
  }
}

const RESERVED_GLOBAL_KEYS = ['appConfig', 'muiTheme', 'fcConfig'];

/**
 * Reads window.timumBookingConfig. Checks the current window first so
 * consumers that set the global on the frame where ConfigHandler actually
 * runs (e.g. direct init(), unit tests) are covered. Falls back to
 * window.parent so the iframe-wrapped paths (initialise() and <TimumBooking>,
 * which both render inside a same-origin srcdoc iframe) still pick up a
 * global set on the host page. Wrapped in try/catch for defensive handling
 * of cross-origin parents.
 */
function readGlobalBookingConfig() {
  try {
    if (typeof window === 'undefined') return undefined;
    if (window.timumBookingConfig) return window.timumBookingConfig;
    if (window.parent && window.parent !== window) {
      return window.parent.timumBookingConfig;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Layers window.timumBookingConfig slices on top of the given configs.
 * Returns new objects — inputs are not mutated.
 *
 * Precedence (lowest to highest) as produced by this helper:
 *   given props  <  global top-level  <  global per-instance (keyed by rootElId)
 *
 * The returned configs are subsequently fed to validateAndPreProcessConfig,
 * which layers URL parameters on top, yielding the full cascade:
 *   defaults < given props < global top-level < global per-instance < URL.
 *
 * The global layer is bypassed entirely when the widget is rendered via the
 * <TimumBooking> React component (isComponent === true) — React users pass
 * props per call and have no use case for a window-level override.
 *
 * Reserved rootElId values ('appConfig', 'muiTheme', 'fcConfig') would collide
 * with the top-level slots and are skipped with a console.warn.
 */
function applyGlobalOverrides(givenAppConfig, givenMuiTheme, givenFcConfig) {
  if (givenAppConfig?.isComponent) {
    return {
      appConfig: givenAppConfig,
      muiTheme: givenMuiTheme,
      fcConfig: givenFcConfig,
    };
  }

  const globalConfig = readGlobalBookingConfig();
  if (!globalConfig) {
    return {
      appConfig: givenAppConfig,
      muiTheme: givenMuiTheme,
      fcConfig: givenFcConfig,
    };
  }

  const rootElId = givenAppConfig?.rootElId;
  let perInstance;
  if (rootElId) {
    if (RESERVED_GLOBAL_KEYS.includes(rootElId)) {
      console.warn(
        `[bookingjs] rootElId "${rootElId}" collides with a reserved top-level ` +
          `slot in window.timumBookingConfig and cannot be used as a ` +
          `per-instance override key. Skipping per-instance lookup.`,
      );
    } else {
      perInstance = globalConfig[rootElId];
    }
  }

  return {
    appConfig: merge(
      {},
      givenAppConfig,
      globalConfig.appConfig,
      perInstance?.appConfig,
    ),
    muiTheme: merge(
      {},
      givenMuiTheme,
      globalConfig.muiTheme,
      perInstance?.muiTheme,
    ),
    fcConfig: merge(
      {},
      givenFcConfig,
      globalConfig.fcConfig,
      perInstance?.fcConfig,
    ),
  };
}

function validateAndPreProcessConfig(givenAppConfig) {
  const result = { ...givenAppConfig };
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(result.queryString || queryString);

  let urlPData = urlParams.get('pData') ?? urlParams.get('pdata');
  let urlPData2 = urlParams.get('pDataPlatform')
    ? {
        platform: urlParams.get('pDataPlatform'),
        id: urlParams.get('pDataId'),
        idPropName: urlParams.get('pDataIdPropName'),
      }
    : undefined;
  const timumCookie = getCookie('PLAY_SESSION');
  let urlChannelKey =
    urlParams.get('channelKey') ?? urlParams.get('channelkey');
  const urlAuthToken = urlParams.get('X-SIGNED-APP-CUSTOMER');
  let urlResId = urlParams.get('resId'); // part of cancel link
  let urlRef = urlParams.getAll('ref');
  let urlTslRefs = urlParams.getAll('tslRefs');
  let urlVirtualTslRefs = urlParams.getAll('virtualTslRefs');
  let urlPrdRefs = urlParams.getAll('prdRefs');
  let urlCulture = urlParams.get('culture');
  let urlPostMessageTarget = urlParams.get('postMessageTarget');
  let urlAllResourcesOption = urlParams.get('allResourcesOption');
  let urlCalendarFrontend = urlParams.get('calendarFrontend');

  // if there is a compressed config, it has priority over manually entered data
  // prevents people fiddling around with their links, as then fiddling causes no changes
  let urlConfig = urlParams.get('conf');
  if (urlConfig) {
    urlConfig = getJwtConfig(urlConfig);
    urlRef = urlConfig.ref || [];
    urlChannelKey = urlConfig.channelKey;
    urlPData = urlConfig.pData;
    urlTslRefs = urlConfig.tslRefs || [];
    urlPrdRefs = urlConfig.prdRefs || [];
    urlCulture = urlConfig.urlCulture;
    urlVirtualTslRefs = urlConfig.virtualTslRefs || [];
    urlPostMessageTarget = urlConfig.postMessageTarget;
    urlAllResourcesOption = urlConfig.allResourcesOption;
    urlCalendarFrontend = urlConfig.calendarFrontend;
  }

  const hash = result.queryHash || window.location.hash;
  const [, , appointmentUuid] = hash?.split('/') ?? [];

  if (urlPData || urlPData2) {
    result.pData = urlPData ?? urlPData2;
  }

  if (urlChannelKey) {
    result.channelKey = urlChannelKey;
  }

  if (urlRef && urlRef.length > 0) {
    result.ref = urlRef;
  }

  if (urlTslRefs && urlTslRefs.length > 0) {
    result.tslRefs = urlTslRefs;
  }

  if (urlVirtualTslRefs && urlVirtualTslRefs.length > 0) {
    result.virtualTslRefs = urlVirtualTslRefs;
  }

  if (urlPrdRefs && urlPrdRefs.length > 0) {
    result.prdRefs = urlPrdRefs;
  }

  if (urlCulture) {
    result.culture = urlCulture;
  }

  if (urlPostMessageTarget) {
    result.postMessageTarget = urlPostMessageTarget;
  }

  if (urlAllResourcesOption) {
    result.allResourcesOption = urlAllResourcesOption;
  }

  if (urlCalendarFrontend) {
    result.calendarFrontend = urlCalendarFrontend;
  }

  // Ensure ref arrays are proper arrays (handles frozen arrays and single values)
  result.ref = ensureArray(result.ref);
  result.tslRefs = ensureArray(result.tslRefs);
  result.prdRefs = ensureArray(result.prdRefs);

  // don't render anything if...
  if (
    !result.alwaysRender &&
    result.isEmbed &&
    result.hiddenForAnonymous &&
    (!result.pData || !result.pData.id)
  ) {
    console.log(
      'timum hides itself. We are in an embed, are set to hide wihtout pData and cannot find none neither in url nor in config',
    );
    return;
  }

  if (
    !result.alwaysRender &&
    (!result.ref || result.ref.length === 0) &&
    (!result.tslRefs || result.tslRefs.length === 0) &&
    (!result.virtualTslRefs || result.virtualTslRefs.length === 0)
  ) {
    // don't render anything if...
    console.log(
      'timum hides itself. No reference in either url or options detected.',
    );
    return;
  }

  // Transform refs to fully qualified format
  result.ref = transformRefArray(
    result.ref,
    result.platform,
    result.prvUuid,
    transformRef,
  );
  result.tslRefs = transformRefArray(
    result.tslRefs,
    result.platform,
    result.prvUuid,
    transformRef,
  );
  result.prdRefs = transformRefArray(
    result.prdRefs,
    result.platform,
    result.prvUuid,
    transformRef,
  );

  if (timumCookie) {
    result.cookie = timumCookie;
  }

  if (urlAuthToken) {
    result.cancelableAppointment = {
      authToken: urlAuthToken,
      appointmentUuid: appointmentUuid,
      customerUuid: urlPData2.id,
      resourceUuid: urlResId,
    };
  }

  if (result.host) {
    setTimumApiHost(result.host);
  }

  if (result.height) {
    result.height = normalizeHeight(result.height);
  }

  return result;
}

/**
 * Fills passed-in configs with missing props without overriding the ones set
 * externally, then applies the permission paywall.
 *
 * Pure function of (given configs, permissions). `permissions` is the tri-state
 * object from selectPermissions ({ theming, fields, localisation } each
 * 'unknown' | 'allowed' | 'denied'). Presentation is gated by the reversibility
 * of applying it:
 *   - theme / fields / footer: OPTIMISTIC — kept during 'unknown', stripped only
 *     on an explicit 'denied' (reverting is a clean re-dispatch).
 *   - localization: PESSIMISTIC — applied only on an explicit 'allowed', because
 *     i18n bundle application (LanguageHandler) is an irreversible additive side
 *     effect; applying it optimistically then discovering 'denied' would leave
 *     custom translations in the i18n instance permanently (a paywall bypass).
 *
 * `givenMuiTheme` is the pre-`primary`-merge theme, so `hasCustomMuiTheme` and
 * `isPresetThemeSync` see the raw customer theme (provenance intact).
 */
const mergeConfigs = (givenAppConfig, givenMuiTheme, givenFcConfig, permissions) => {
  const hasCustomMuiTheme = Object.keys(givenMuiTheme).length > 0;
  const hasCustomAppConfig = Object.keys(givenAppConfig).length > 0;
  let mergedAppConfig = merge({}, primary.appConfig, givenAppConfig);
  let mergedMuiTheme = merge({}, primary.muiTheme, givenMuiTheme);
  let mergedFcConfig = merge({}, primary.fcConfig, givenFcConfig);
  let tld = getVisibleTld();

  // Orders input fields according to their index. Uses the shared helper so
  // the channel path and the product-override path (mergeProductOverride)
  // order identically. The previous local counting sort only visited indexes
  // 0..fieldCount-1, so any field carrying a larger or gapped index — trivially
  // produced by the admin editor's free-form "Display Order" input — was
  // silently dropped from the form instead of merely being misordered.
  //
  // `removeNullFields` is explicit here because that same counting sort also
  // happened to strip null-valued keys (its `value && ...` guard). Nulls are
  // the editor's tombstone for a disabled standard field, so they must keep
  // disappearing — VariableForm would throw on a null field definition.
  mergedAppConfig.fields = sortFieldsByIndex(
    removeNullFields(mergedAppConfig.fields),
  );

  // remove fields if they have been set to undefined
  removeUndefinedFields(givenAppConfig.fields, mergedAppConfig.fields);

  // if customer defined fontSource, remove our default font faces.
  if (mergedMuiTheme?.typography?.fontSource) {
    delete mergedMuiTheme.typography.fontFaces;
  }

  // Apply the paywall via the shared per-slice policy (see isSlicePermitted).
  // Each block reverts a merged slice to the default when the slice isn't permitted.
  if (!isSlicePermitted('localisation', permissions.localisation) && hasCustomAppConfig) {
    mergedAppConfig.localization = primary.appConfig.localization;
  }
  if (!isSlicePermitted('fields', permissions.fields) && hasCustomAppConfig) {
    mergedAppConfig.fields = primary.appConfig.fields;
  }

  if (!isSlicePermitted('theming', permissions.theming) && hasCustomAppConfig) {
    mergedAppConfig.hideTimumFooter = primary.appConfig.hideTimumFooter;
  }

  if (
    hasCustomMuiTheme &&
    !isSlicePermitted('theming', permissions.theming) &&
    !isPresetThemeSync(givenMuiTheme)
  ) {
    mergedMuiTheme = primary.muiTheme;
  }

  if (mergedAppConfig.postMessageTarget) {
    addPostMessageCallbacks(mergedAppConfig);
  }

  // adapt default mobile country code and preferred country based on tld
  applyMobileDefaults(tld, mergedAppConfig, givenAppConfig);

  return {
    mergedAppConfig,
    mergedMuiTheme,
    mergedFcConfig,
  };
};

// Excludes the regenerated `callbacks` (addPostMessageCallbacks rebuilds fresh
// function identities on every merge) and JSX `interfaces` from the appConfig
// comparison, so those never spuriously mark appConfig as "changed". Safe:
// callbacks are a pure function of `postMessageTarget`, which IS compared — if
// it changes, the rest of appConfig differs and we dispatch anyway.
//
// INVARIANT: the merged appConfig must remain a deterministic pure function of
// its inputs; only `callbacks`/`interfaces` may carry unstable identities. If a
// future change adds an always-changing field (Date.now(), a random id, a fresh
// inline function, …) to mergedAppConfig, this key would differ on every
// rerender and silently defeat the skip optimization — add it to the omit list.
function appConfigComparisonKey(appConfig) {
  return cloneDeep(omit(appConfig, ['callbacks', 'interfaces']));
}

// Dispatches only the config slices whose merged value actually changed since
// the previous dispatch. A 'rerender' that touches one slice (e.g. muiTheme)
// then leaves the other slices' store references intact, so selectAppConfig's
// memo holds and no appConfig consumer re-renders or refetches needlessly.
//
// `last` carries the previous comparison snapshots across renders (a ref),
// seeded null so the initial mount dispatches all three. Snapshots are cloned
// BEFORE dispatch because setAppConfig/setMuiTheme mutate their payload (strip
// fields/interfaces/callbacks, normalize pData, extract pxToRem) — storing the
// live object would corrupt the next comparison. A genuine value change (incl.
// a paywall correction when permissions resolve) still differs and dispatches.
function dispatchConfigs({ appConfig, muiTheme, fcConfig, dispatch, last }) {
  const fcConfigKey = cloneDeep(fcConfig);
  if (!last.fcConfig || !isEqual(last.fcConfig, fcConfigKey)) {
    last.fcConfig = fcConfigKey;
    dispatch(setFcConfig(fcConfig));
  }

  const appConfigKey = appConfigComparisonKey(appConfig);
  if (!last.appConfig || !isEqual(last.appConfig, appConfigKey)) {
    last.appConfig = appConfigKey;
    dispatch(setAppConfig(appConfig));
  }

  const muiThemeKey = cloneDeep(muiTheme);
  if (!last.muiTheme || !isEqual(last.muiTheme, muiThemeKey)) {
    last.muiTheme = muiThemeKey;
    dispatch(setMuiTheme(muiTheme));
  }
}

const ConfigHandler = ({
  muiTheme: muiThemeControlled = {},
  fcConfig: fcConfigControlled = {},
  appConfig: appConfigControlled = {},
  children,
}) => {
  const dispatch = useDispatch();
  // Tri-state permissions ({ theming, fields, localisation }). Memoized on the
  // provider reference, so shallowEqual keeps a stable identity here too.
  const permissions = useSelector(selectPermissions, shallowEqual);

  // Single source of truth for the runtime input configs. Seeded from props;
  // thereafter written only by the postMessage handler (matching the previous
  // useState-mirror behavior: parent prop updates after mount are ignored).
  const [input, setInput] = React.useState({
    appConfig: appConfigControlled,
    muiTheme: muiThemeControlled,
    fcConfig: fcConfigControlled,
  });

  // Per-slice snapshots of the last dispatched config, so dispatchConfigs can
  // skip re-dispatching a slice whose value hasn't changed (see dispatchConfigs).
  // Seeded null → the first run dispatches all three.
  const lastDispatchedRef = React.useRef({
    appConfig: null,
    muiTheme: null,
    fcConfig: null,
  });

  // Snapshot of the last merged data-fetch-relevant deps, so a 'rerender' resets
  // the selected product ONLY when the product set can actually have changed
  // (see the reset in effect (1)). Seeded null → the first run never resets.
  const prevFetchDepsRef = React.useRef(null);
  // Reset the snapshot on unmount so a remount re-treats its first run as
  // initial (StrictMode / instance reuse safety), matching DataFetchHandler.
  useEffect(() => () => {
    prevFetchDepsRef.current = null;
  }, []);

  // (1) THE ONLY merge + dispatch site. Runs on input change and on permission
  // resolution. Because the gate is tri-state and optimistic, an unknown-window
  // run keeps the custom config, so re-running when flags resolve is either a
  // no-op (allowed) or a single clean correction (denied).
  useEffect(() => {
    // window.timumBookingConfig is read fresh here (not a dependency): the
    // global is expected to be set before init and not hot-swapped.
    const {
      appConfig: globalizedAppConfig,
      muiTheme: globalizedMuiTheme,
      fcConfig: globalizedFcConfig,
    } = applyGlobalOverrides(input.appConfig, input.muiTheme, input.fcConfig);
    const processedConfig = validateAndPreProcessConfig(globalizedAppConfig);

    if (!processedConfig) return;

    const { mergedAppConfig, mergedMuiTheme, mergedFcConfig } = mergeConfigs(
      processedConfig,
      globalizedMuiTheme,
      globalizedFcConfig,
      permissions,
    );

    // Invalidate the selected product IFF a data-fetch-relevant field actually
    // changed — the only case where the available product set (and thus the
    // validity of the current selection) can change. A presentational rerender
    // (muiTheme, culture, labels, callbacks, calendarFrontend, …) leaves these
    // untouched and correctly PRESERVES the selection. Compared on the MERGED
    // config because several of these fields are derived here (transformed refs,
    // URL-built cancelableAppointment, defaulted allResourcesOption/host) — the
    // same basis DataFetchHandler uses to gate refetches, so the reset fires
    // exactly when the data is refetched. The ref-guard keeps this off the
    // initial mount and idempotent under StrictMode's double-invoke; it also
    // no-ops on permission resolution (fetch deps don't depend on permissions).
    const fetchDeps = pickDataFetchDeps(mergedAppConfig);
    if (
      prevFetchDepsRef.current !== null &&
      !isEqual(prevFetchDepsRef.current, fetchDeps)
    ) {
      dispatch(selectProduct(undefined));
    }
    prevFetchDepsRef.current = fetchDeps;

    dispatchConfigs({
      appConfig: mergedAppConfig,
      muiTheme: mergedMuiTheme,
      fcConfig: mergedFcConfig,
      dispatch,
      last: lastDispatchedRef.current,
    });
    // `permissions` has a stable identity (memoized selectPermissions), so
    // depending on the object does not over-fire.
  }, [input, permissions, dispatch]);

  // (2) postMessage listener. Thin: origin check → update input. The product
  // invalidation used to live here (unconditional on every 'rerender'); it now
  // lives in effect (1), gated on the merged data-fetch deps, so a presentational
  // rerender preserves the selection. The functional updater with per-slice
  // fallback avoids stale-closing over the seed input and preserves slices absent
  // from a partial payload.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.type !== 'rerender') return;
      if (!allowedOriginRegex.test(event.origin)) {
        console.error(
          'ConfigHandler received a message from an unexpected origin:',
          event.origin,
        );
        return;
      }
      setInput((prev) => ({
        appConfig: event.data.appConfig || prev.appConfig,
        muiTheme: event.data.muiTheme || prev.muiTheme,
        fcConfig: event.data.fcConfig || prev.fcConfig,
      }));
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // Only setInput (stable) is used now; the listener attaches once.
  }, []);

  // (3) 'ready' handshake. Posts on mount, on 'rerender' (input change), AND on
  // permission resolution. The permission-resolution re-post is the load-bearing
  // retry for a preview parent that attaches its listener after the iframe's
  // first 'ready' — `input` alone does NOT change when the public-data fetch
  // resolves the flags, so `permissions` must be in the deps. Repeated 'ready'
  // is idempotent on the parent (verified against core BookingJsPreview).
  useEffect(() => {
    window?.parent?.postMessage(
      { origin: 'bookingjs', type: 'ready' },
      input.appConfig?.postMessageTarget || '*',
    );
  }, [input, permissions]);

  return children;
};

ConfigHandler.displayName = 'ConfigHandler';
export default ConfigHandler;
